import { useState, useEffect } from "react";
import Sidebar from "../../../shared/components/Sidebar";
import { TextField, Button, CircularProgress, FormControl, InputLabel, Select, MenuItem, Snackbar, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../../../shared/components/Backbtn";
import useSingleBiodataViewModel from "../hooks/useSingleBiodataViewModel";
import ImageDropzone from "../../../shared/components/ImageDropzone";

const EditBiodata = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { biodata, loading, error, updateBiodata } = useSingleBiodataViewModel(id);

  const [fullName, setFullName] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [caste, setCaste] = useState("");
  const [height, setHeight] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [income, setIncome] = useState("");
  const [qualification, setQualification] = useState("");
  const [workplace, setWorkplace] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [status, setStatus] = useState("");
  const [selectedImageFiles, setSelectedImageFiles] = useState([]);
  const [existingImageUrls, setExistingImageUrls] = useState([]);
  // const [loading, setLoading] = useState(true); // Managed by ViewModel

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    if (biodata) {
      setFullName(biodata.fullName || "");
      setAgeRange(biodata.ageRange || "");
      setCaste(biodata.caste || "");
      setHeight(biodata.height || "");
      setDob(biodata.dob ? biodata.dob.toISOString().split('T')[0] : ""); // Format Date to YYYY-MM-DD
      setGender(biodata.gender || "");
      setIncome(biodata.income || "");
      setQualification(biodata.qualification || "");
      setWorkplace(biodata.workplace || "");
      setMaritalStatus(biodata.maritalStatus || "");
      setStatus(biodata.status || "Created");
      setExistingImageUrls(biodata.imagePaths || []);
    } else if (!loading && error) {
      // If there's an error, show it and redirect
      setSnackbarMessage(error);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setTimeout(() => navigate("/biodata"), 2000);
    } else if (!loading && !error && !biodata) {
      // If not loading, no error, but biodata is null, means not found
      setSnackbarMessage("Biodata profile not found!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setTimeout(() => navigate("/biodata"), 2000);
    }
  }, [biodata, loading, error, navigate]);

  const handleSubmit = async () => {
    if (!fullName || !gender || !maritalStatus) {
      setSnackbarMessage("Please fill in all required fields: Full Name, Gender, and Marital Status.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    try {
      const updates = {
        fullName,
        ageRange,
        caste,
        height,
        dob: dob ? new Date(dob) : null,
        gender,
        income,
        qualification,
        workplace,
        maritalStatus,
        status,
      };

      // Only include imageFiles in updates if new files are selected
      if (selectedImageFiles.length > 0) {
        updates.imageFiles = selectedImageFiles;
      } else if (existingImageUrls.length === 0) {
        // If no new images and no existing images, explicitly set imagePaths to empty
        updates.imageFiles = null; // Signal to repository to clear images
      }

      const success = await updateBiodata(updates);

      if (success) {
        setSnackbarMessage("Profile updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => navigate("/biodata"), 1500);
      } else {
        // Error message already set by ViewModel
        setSnackbarMessage(error?.message || "Failed to update profile. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Error updating biodata:", err);
      
      // Check for specific Firebase Storage errors
      if (err.code === 'storage/quota-exceeded') {
        setSnackbarMessage("Storage quota exceeded. Please contact administrator or try updating without uploading new images.");
      } else if (err.message) {
        setSnackbarMessage(`Failed to update profile: ${err.message}`);
      } else {
        setSnackbarMessage("Failed to update profile. Please try again.");
      }
      
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleFilesChange = (files) => {
    setSelectedImageFiles(files);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="dashboard--content">
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="dashboard--content">
        <Snackbar
          open={snackbarOpen || !!error}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          message={snackbarMessage || error?.message || error}
          severity={snackbarSeverity}
        />
        <div className="flex justify-end gap-3 mb-4">
          <BackButton tooltip="Back" />
        </div>
        <div className="section">
          <h2 className="text-2xl font-bold mb-4">Edit Profile</h2>

          <FormControl fullWidth margin="normal" variant="outlined">
            <TextField
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              variant="outlined"
              required
            />
          </FormControl>

          <FormControl fullWidth margin="normal" variant="outlined">
            <TextField
              label="Age Range"
              value={ageRange}
              onChange={(e) => setAgeRange(e.target.value)}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal" variant="outlined">
            <TextField
              label="Caste"
              value={caste}
              onChange={(e) => setCaste(e.target.value)}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal" variant="outlined">
            <TextField
              label="Height"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal" variant="outlined">
            <TextField
              label="Date of Birth"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal" variant="outlined" required error={!gender}>
            <InputLabel id="gender-label">Gender *</InputLabel>
            <Select
              labelId="gender-label"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              label="Gender *"
            >
              <MenuItem value="">
                <em>Select Gender</em>
              </MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Non-binary">Non-binary</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
              <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" variant="outlined">
            <TextField
              label="Income"
              value={income}
              onChange={(e) => setIncome(e.target.value)}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal" variant="outlined">
            <TextField
              label="Qualification"
              value={qualification}
              onChange={(e) => setQualification(e.target.value)}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal" variant="outlined">
            <TextField
              label="Workplace"
              value={workplace}
              onChange={(e) => setWorkplace(e.target.value)}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal" variant="outlined" required>
            <InputLabel id="marital-status-label">Marital Status *</InputLabel>
            <Select
              labelId="marital-status-label"
              value={maritalStatus}
              onChange={(e) => setMaritalStatus(e.target.value)}
              label="Marital Status *"
            >
              <MenuItem value=""><em>None</em></MenuItem>
              <MenuItem value="Single">Single</MenuItem>
              <MenuItem value="Married">Married</MenuItem>
              <MenuItem value="Divorced">Divorced</MenuItem>
              <MenuItem value="Widowed">Widowed</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" variant="outlined">
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="Created">Created</MenuItem>
              <MenuItem value="Reported">Reported</MenuItem>
              <MenuItem value="Accepted">Accepted</MenuItem>
              <MenuItem value="Hide">Hide</MenuItem>
              <MenuItem value="Show">Show</MenuItem>
            </Select>
          </FormControl>

          {/* Existing Images Display */}
          {existingImageUrls.length > 0 && (
            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
              <Typography variant="body2" sx={{ mb: 1, color: '#666', fontWeight: 500 }}>
                Current Images
              </Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {existingImageUrls.map((url, index) => (
                  <div key={`existing-${index}`} style={{ position: 'relative' }}>
                    <img src={url} alt={`Existing ${index}`} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} />
                    <span style={{ position: 'absolute', top: '4px', left: '4px', background: 'rgba(0,0,0,0.6)', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem' }}>
                      Current
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ImageDropzone
            selectedFiles={selectedImageFiles}
            onFilesChange={handleFilesChange}
            multiple={true}
            label="Upload New Images (Optional)"
          />

          <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px', marginLeft: '14px' }}>
            Note: If storage quota is exceeded, you can update profile without uploading new images.
          </p>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ mt: 3 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Update Profile'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditBiodata;
